import { z } from "zod";

/**
 * Billing, seats, waitlist and access-grant contracts (server: apps/server/src/billing/,
 * docs/paywall.md). Money is always integer cents.
 */

export const SubscriptionStateSchema = z.enum([
  "pending_admission",
  "admitted",
  "active",
  "past_due",
  "canceling",
  "ended",
  "refunded_removed",
  "declined",
  "hold_expired",
]);
export type SubscriptionState = z.infer<typeof SubscriptionStateSchema>;

export const PaymentsModeSchema = z.enum(["off", "fake", "stripe"]);

export const AccessGrantKindSchema = z.enum(["volunteer", "developer", "tester"]);
export type AccessGrantKind = z.infer<typeof AccessGrantKindSchema>;

/** `GET /v1/site`: public, cached ~30 s, counts only. */
export const SiteInfoSchema = z.object({
  reservationPriceCents: z.number().int().optional(),
  includedDays: z.number().int().optional(),
  productName: z.string(),
  priceCents: z.number().int(),
  currency: z.string(),
  interval: z.string(),
  seats: z.object({
    cap: z.number().int(),
    /** Seats held by subscribers plus seats reserved for waitlist offers. */
    taken: z.number().int(),
    open: z.number().int(),
  }),
  payments: PaymentsModeSchema,
  /** True when new players should join the waitlist instead of subscribing. */
  waitlistOpen: z.boolean(),
});
export type SiteInfo = z.infer<typeof SiteInfoSchema>;

export const WaitlistStatusSchema = z.enum(["waiting", "offered", "claimed", "expired", "left"]);

/** Why an account may not play: the access decision's `reason`. */
export const ACCESS_DENY_REASONS = [
  "unverified",
  "no_subscription",
  "pending_admission",
  "payment_required",
  "ended",
  "banned",
] as const;
export type AccessDenyReason = (typeof ACCESS_DENY_REASONS)[number];

export const AccessDecisionSchema = z.union([
  z.object({
    ok: z.literal(true),
    via: z.enum(["subscription", "grant", "admin", "bot"]),
  }),
  z.object({
    ok: z.literal(false),
    reason: z.enum(ACCESS_DENY_REASONS),
  }),
]);

/**
 * Every reason the server gives when it shuts an account out of the game: the close reason of a
 * game socket closed with `ACCESS_CLOSE_CODE`, the `reason` of a `ws-ticket` or character 403.
 * The client has words for each (apps/client/src/game/access.ts).
 */
export type AccessKickReason =
  | AccessDenyReason
  | `subscription_${SubscriptionState}`
  | "removed"
  | "grant_revoked"
  | "account_deleted";
export const ACCESS_KICK_REASONS: readonly AccessKickReason[] = [
  ...ACCESS_DENY_REASONS,
  ...SubscriptionStateSchema.options.map((s) => `subscription_${s}` as const),
  "removed",
  "grant_revoked",
  "account_deleted",
];

/** `GET /v1/billing/status` for the signed-in account. */
export const BillingStatusSchema = z.object({
  reservation: z
    .object({
      status: z.enum([
        "pending",
        "paid",
        "refunding",
        "refunded",
        "accepting",
        "started",
        "failed",
        "disputed",
      ]),
      amountCents: z.number().int(),
      refundable: z.boolean(),
      includedDays: z.number().int(),
      trialEndsAt: z.string().nullable(),
    })
    .optional(),
  payments: PaymentsModeSchema,
  access: AccessDecisionSchema,
  subscription: z
    .object({
      state: SubscriptionStateSchema,
      priceCents: z.number().int(),
      currency: z.string(),
      interval: z.string(),
      currentPeriodEnd: z.string().nullable(),
      cancelAtPeriodEnd: z.boolean(),
      holdExpiresAt: z.string().nullable(),
    })
    .nullable(),
  ogNumber: z.number().int().nullable(),
  cohortRole: z.string().nullable(),
  /** Card saved but refused (e.g. a card tied to a removed account). */
  setupBlocked: z.boolean(),
  /**
   * Why: `card` = this card is refused (add a different one), `account` = the account was
   * removed, `other` = e.g. no card came back. Null when not blocked.
   */
  setupBlockedReason: z.enum(["card", "account", "other"]).nullable(),
  waitlist: z
    .object({
      status: WaitlistStatusSchema,
      /** 1-based place in line while waiting. */
      position: z.number().int().nullable(),
      offerExpiresAt: z.string().nullable(),
    })
    .nullable(),
  /** May start `POST /v1/billing/setup` right now. */
  canSubscribe: z.boolean(),
  grant: z.object({ kind: AccessGrantKindSchema, expiresAt: z.string().nullable() }).nullable(),
});
export type BillingStatus = z.infer<typeof BillingStatusSchema>;

/** `POST /v1/billing/setup`. The separate auto-renewal consent box (California ARL) is required. */
export const BillingSetupRequestSchema = z.object({ autoRenewConsent: z.literal(true) });

export const RedirectResponseSchema = z.object({ redirectUrl: z.string() });

/** Admin money actions all require a reason (written to the audit log). */
const reason = z.string().trim().min(3).max(500);
export const AdminReasonSchema = z.object({ reason });
export const AdminRefundSchema = z.object({ reason, cancel: z.boolean() });
export const AdminCancelSchema = z.object({ reason, now: z.boolean().optional() });
export const AdminGrantSchema = z.object({
  reason,
  kind: AccessGrantKindSchema,
  /** Omit for a grant without expiry. */
  expiresInDays: z.number().int().min(1).max(3650).optional(),
});
export const AdminRevokeGrantSchema = z.object({ reason, grantId: z.number().int().positive() });
export const AdminInviteCreateSchema = z.object({
  reason,
  kind: AccessGrantKindSchema,
  uses: z.number().int().min(1).max(1000),
  expiresInDays: z.number().int().min(1).max(365).optional(),
  /** Optional address to email the link to. */
  email: z.email().max(254).optional(),
});
export const SimulatorAdvanceSchema = z.object({
  days: z.number().int().min(1).max(400).optional(),
  months: z.number().int().min(1).max(24).optional(),
});
export const SimulatorAccountSchema = z.object({ accountId: z.number().int().positive() });
export const SimulatorReplaySchema = z.object({ eventId: z.string().min(1).max(200) });
export const SimulatorShuffleSchema = z.object({ count: z.number().int().min(1).max(50) });
